import { defineStore } from 'pinia'
import { ACTING_COACH_ID_KEY, ACTING_COACH_NAME_KEY } from '@/constants/viewAs'

export const useUserStore = defineStore('user', {
  state: () => {
    const actingCoachIdRaw = localStorage.getItem(ACTING_COACH_ID_KEY)
    return {
      email: '',
      displayName: '',
      roles: [] as string[],
      isCoach: false,
      panelRole: '' as 'ADMIN' | 'COACH' | '',
      isAuthenticated: false,
      // When an admin is "viewing as" a coach, these hold that coach's id/name and
      // every coach-scoped request carries the X-Acting-Coach-Id header.
      actingCoachId: actingCoachIdRaw ? Number(actingCoachIdRaw) : (null as number | null),
      actingCoachName: localStorage.getItem(ACTING_COACH_NAME_KEY) || '',
    }
  },

  getters: {
    isAdmin: (state) => state.panelRole === 'ADMIN',
    hasAdminRole: (state) => state.roles.includes('ADMIN'),
    hasCoachRole: (state) => state.roles.includes('COACH'),
    isViewingAsCoach: (state) => state.actingCoachId != null,
  },

  actions: {
    setUser(email: string, displayName: string, roles: string[], panelRole: 'ADMIN' | 'COACH') {
      this.email = email
      this.displayName = displayName
      this.roles = roles
      this.isCoach = roles.includes('COACH')
      this.panelRole = panelRole
      this.isAuthenticated = true
      localStorage.setItem('panelRole', panelRole)
      // Only an admin can act as a coach. Clear any stale acting-coach left in
      // localStorage (e.g. a previous admin session closed without logging out),
      // otherwise a non-admin's requests would carry X-Acting-Coach-Id and be
      // rejected with 403, locking them out of the panel.
      if (!this.hasAdminRole) {
        this.resetActingCoachState()
      }
    },

    setPanelRole(role: 'ADMIN' | 'COACH') {
      this.panelRole = role
      localStorage.setItem('panelRole', role)
    },

    // Enter "view as coach": render the panel as the coach and scope every
    // coach-scoped request to them. Only meaningful for admins.
    setActingCoach(coachId: number, coachName: string) {
      this.actingCoachId = coachId
      this.actingCoachName = coachName
      localStorage.setItem(ACTING_COACH_ID_KEY, String(coachId))
      localStorage.setItem(ACTING_COACH_NAME_KEY, coachName)
      this.setPanelRole('COACH')
    },

    // Exit "view as coach" and return to the admin panel.
    clearActingCoach() {
      this.resetActingCoachState()
      this.setPanelRole('ADMIN')
    },

    // Clears acting-coach state and its persisted keys without touching panelRole.
    resetActingCoachState() {
      this.actingCoachId = null
      this.actingCoachName = ''
      localStorage.removeItem(ACTING_COACH_ID_KEY)
      localStorage.removeItem(ACTING_COACH_NAME_KEY)
    },

    clearUser() {
      this.email = ''
      this.displayName = ''
      this.roles = []
      this.isCoach = false
      this.panelRole = ''
      this.isAuthenticated = false
      this.resetActingCoachState()
      localStorage.removeItem('panelRole')
    },
  },
})
